import React, { useState } from 'react';
import { parkingManagementApi } from '../../api/parkingManagementApi';

interface MonthlyVehicleImportModalProps {
  isOpen: boolean;
  buildingId: string;
  buildingName: string;
  t: (key: string) => string;
  onClose: () => void;
  onImported: (result: { created: number; updated: number; skipped: number; errors: string[] }) => void;
}

export const MonthlyVehicleImportModal: React.FC<MonthlyVehicleImportModalProps> = ({
  isOpen,
  buildingId,
  buildingName,
  t,
  onClose,
  onImported,
}) => {
  const [importFile, setImportFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleDownloadSample = async () => {
    try {
      setError(null);
      await parkingManagementApi.exportMonthlyVehicleSample(buildingId);
    } catch {
      setError(t('failedToExportSample'));
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      setError(t('pleaseSelectFile'));
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('buildingId', buildingId);
      const result = await parkingManagementApi.importMonthlyVehicles(formData);
      onImported(result);
      setImportFile(null);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || t('importFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <div>
            <h2 className="text-xl font-bold text-gray-800">{t('importMonthlyVehicles')}</h2>
            <p className="text-sm text-gray-500 mt-1">{buildingName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-200 rounded-full"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">{error}</div>
          )}

          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-700 mb-3">{t('step1DownloadSample')}</h3>
            <p className="text-gray-600 mb-3 text-sm leading-relaxed">
              {t('importMonthlyVehiclesSampleDescription')}
            </p>
            <button
              type="button"
              onClick={handleDownloadSample}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg"
            >
              {t('downloadSampleFile')}
            </button>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-700 mb-3">{t('step2UploadFile')}</h3>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="hidden"
                id="monthly-vehicle-import-file"
              />
              <label htmlFor="monthly-vehicle-import-file" className="cursor-pointer flex flex-col items-center gap-2">
                <span className="text-gray-600 text-sm">
                  {importFile ? importFile.name : t('clickToSelectFile')}
                </span>
                <span className="text-xs text-gray-500">{t('excelRecommended')}</span>
              </label>
            </div>
          </div>

          <div className="mb-2">
            <h3 className="text-lg font-medium text-gray-700 mb-3">{t('requiredFields')}</h3>
            <div className="bg-gray-50 p-4 rounded-lg text-sm space-y-2">
              <p>
                <strong className="text-red-600">*</strong> MÃ CĂN HỘ, LOẠI THẺ
              </p>
              <p>• {t('importCardsTypeHint')}</p>
              <p>
                • SỐ THẺ, MÃ THẺ, BIỂN SỐ XE, MẪU XE, NGÀY BẮT ĐẦU, NGÀY HẾT HẠN, NGÀY ĐĂNG KÝ, ĐIỆN THOẠI,
                CHỦ XE, SỐ Ô ĐẬU, TRẠNG THÁI, <strong>PHÍ THÁNG</strong> (all-in, để trống = theo danh mục phí; 0 =
                miễn phí), GHI CHÚ
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
            {t('cancel')}
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={loading || !importFile}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? t('processing') : t('import')}
          </button>
        </div>
      </div>
    </div>
  );
};
